import { useEffect, useState } from 'react';
import CommonApi from '../../Api/CommonApi';
import { OldManualList } from '../../Models/OldManualList';
import Loader from '../Common/Loader';
import OldManualEditModal from './OldManualEdit';
import { toast } from 'react-toastify';

export default function OldManualListPage() {
  const [manuals, setManuals] = useState<OldManualList[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedManualId, setSelectedManualId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    fetchManuals();
  }, []);

  const fetchManuals = () => {
    setIsLoading(true);
    CommonApi.getAllOldManuals()
      .then((res) => setManuals(res.data))
      .catch((err) => console.error('Failed to load manuals', err))
      .finally(() => setIsLoading(false));
  };

  const handleEdit = (id: number) => {
    setSelectedManualId(id.toString());
    setModalOpen(true);
  };

  const handleAdd = () => {
    setSelectedManualId('0'); // you can use '0' or a convention to trigger an "empty" manual
    setModalOpen(true);
  };

  const handleDelete = (id: number) => {
    CommonApi.deleteOldManual(id)
      .then(() => {
        fetchManuals();
        toast.success('Manual Deleted');
      })
      .catch(() => toast.success('Error Deleting'))
      .finally();
  };

  return (
    <div className='p-4'>
      <div className='flex justify-between items-center mb-4'>
        <h1 className='text-2xl font-bold'>Old Manuals</h1>
        <button onClick={handleAdd} className='buttonMain p-3'>
          + Add New Manual
        </button>
      </div>
      <div className='flex flex-col sm:flex-row sm:items-center gap-2 mb-4'>
        <input
          type='text'
          placeholder='Search...'
          className='w-full sm:w-80 p-2 border rounded'
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        {searchText && (
          <button
            onClick={() => setSearchText('')}
            className='px-3 py-2 bg-gray-300 rounded hover:bg-gray-400 text-sm'
          >
            Clear
          </button>
        )}
      </div>

      {isLoading ? (
        <Loader />
      ) : (
        <table className='w-full border border-gray-200'>
          <thead className='bg-gray-100 text-left'>
            <tr>
              <th className='p-2'>Edit</th>
              <th className='p-2'>ID</th>
              <th className='p-2'>Manufacturer</th>
              <th className='p-2'>SubCategory</th>
              <th className='p-2'>File Name</th>
              <th className='p-2'>Delete</th>
            </tr>
          </thead>
          <tbody>
            {manuals
              .filter((manual) => {
                const search = searchText.toLowerCase();
                return (
                  manual.fileName?.toLowerCase().includes(search) ||
                  manual.subCategoryName?.toLowerCase().includes(search) ||
                  manual.manufacturerName?.toLowerCase().includes(search)
                );
              })
              .map((manual) => (
                <tr key={manual.id} className='border-t'>
                  <td className='p-2'>
                    <button
                      onClick={() => handleEdit(manual.id)}
                      className='text-blue-600 hover:underline'
                    >
                      Edit
                    </button>
                  </td>
                  <td className='p-2'>{manual.id}</td>
                  <td className='p-2'>{manual.manufacturerName}</td>
                  <td className='p-2'>{manual.subCategoryName}</td>
                  <td className='p-2'>{manual.fileName}</td>
                  <td className='p-2'>
                    <button
                      onClick={() => handleDelete(manual.id)}
                      className='text-blue-600 hover:underline'
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            {manuals.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className='p-2 italic text-gray-500 text-center'
                >
                  No manuals found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {modalOpen && selectedManualId && (
        <OldManualEditModal
          isOpen={modalOpen}
          manualId={selectedManualId}
          onClose={() => {
            setModalOpen(false);
            setSelectedManualId(null);
          }}
          onSaved={fetchManuals}
        />
      )}
    </div>
  );
}
